import { useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/shared/hooks/use-auth'
import { useUser } from '@/shared/hooks/use-user'
import { useOnboardingTrace } from '@/shared/hooks/queries/use-onboarding-trace'
import { getJwt } from '@/shared/lib/betterAuth/get-jwt'
import {
  CACHE_STALE_TIME,
  CACHE_GC_TIME,
  QUERY_NETWORK_MODE,
  RENTERS_QUERY_KEYS,
} from '@/shared/constants/cache'
import { getRentersPolicy } from '@/services/renters.services'
import { formatDate } from '@/shared/helpers/format-date'
import { ROUTES } from '@/shared/constants/routes'
import { sendEvent } from '@/shared/hooks/use-analytics'
import type { RentersPolicy } from '@/shared/types/global.types'
import { useLanguage } from '@/shared/lib/i18n'
import { postMessageToParent } from '@/shared/helpers/post-message'
import { ONBOARDING_MESSAGE } from '@/shared/constants/onboarding'

const ONBOARDING_STEP_COUNT = 6

const STEP_COMPLETE_EVENTS: string[] = [
  'ob_renters_screen_one_completed',
  'ob_renters_screen_two_completed',
  'ob_renters_screen_three_completed',
  'ob_renters_screen_four_completed',
  'ob_renters_screen_five_completed',
  'ob_renters_screen_six_completed',
]

export function useOnboardingRentersPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user: authUser, loading } = useAuth()
  const { user: userProfile } = useUser()
  const userId = userProfile?.id

  const { currentLanguage } = useLanguage()
  const [isModalOpen, setIsModalOpen] = useState(true)
  const [currentStep, setCurrentStep] = useState(0)
  const isOnboardingInitialized = useRef(false)

  useEffect(() => {
    postMessageToParent(ONBOARDING_MESSAGE.SHOW_ONBOARDING_BUTTON_CLOSE)
  }, [])

  const {
    data: onboardingTrace,
    isFetched: isOnboardingTracesFetched,
    createMutation,
    updateMutation,
  } = useOnboardingTrace({ product_type: 'renters' }, !!userId)

  useEffect(() => {
    if (
      !isOnboardingTracesFetched ||
      !userId ||
      isOnboardingInitialized.current
    )
      return
    isOnboardingInitialized.current = true

    if (!onboardingTrace) {
      createMutation.mutate('renters')
      return
    }

    setCurrentStep(
      Math.min(onboardingTrace.step - 1, ONBOARDING_STEP_COUNT - 1),
    )
  }, [isOnboardingTracesFetched, userId, onboardingTrace])

  const closeModal = useCallback(() => {
    postMessageToParent(ONBOARDING_MESSAGE.COMPLETE_ONBOARDING)
    navigate({ to: ROUTES.HOME })
    setIsModalOpen(false)
  }, [navigate])

  const handleNext = useCallback(() => {
    const event = STEP_COMPLETE_EVENTS[currentStep]
    if (event) sendEvent(event)

    if (currentStep === ONBOARDING_STEP_COUNT - 1) {
      sendEvent('ob_renters_completed')
      if (onboardingTrace) {
        updateMutation.mutate({
          id: onboardingTrace.id,
          body: { status: 'completed' },
        })
      }
      setIsModalOpen(false)
      navigate({
        to: ROUTES.ONBOARDING_RENTERS_FORM,
      })
      return
    }

    const next = currentStep + 1
    if (onboardingTrace) {
      updateMutation.mutate({
        id: onboardingTrace.id,
        body: { step: next + 1 },
      })
    }
    setCurrentStep(next)
  }, [currentStep, onboardingTrace, updateMutation, navigate])

  const handleBack = useCallback(() => {
    const prev = Math.max(0, currentStep - 1)
    if (onboardingTrace) {
      updateMutation.mutate({
        id: onboardingTrace.id,
        body: { step: prev + 1 },
      })
    }
    setCurrentStep(prev)
  }, [currentStep, onboardingTrace, updateMutation])

  const handleManageCoverage = useCallback(() => {
    navigate({ to: ROUTES.PAYMENT })
  }, [navigate])

  const { data: rentersPolices, isLoading: policiesLoading } = useQuery<
    RentersPolicy[]
  >({
    queryKey: RENTERS_QUERY_KEYS.policies,
    queryFn: async () => {
      const jwt = await getJwt()
      return getRentersPolicy(jwt)
    },
    enabled: !loading && !!authUser,
    staleTime: CACHE_STALE_TIME,
    gcTime: CACHE_GC_TIME,
    networkMode: QUERY_NETWORK_MODE,
  })

  const policy = rentersPolices?.[0]

  const policyId =
    '#' + (userProfile?.ciUserId ?? userProfile?.insuranceId ?? '')

  const dwellingType = policy?.dwellingType
    ? t(`onboarding.renters.dwellingTypes.${policy.dwellingType}`)
    : ''

  const startAt = formatDate(policy?.startAt)

  const additionalCoverageTypes = (policy?.additionalCoverages ?? [])
    .map((c) => c.type)
    .filter((t): t is NonNullable<typeof t> => t !== undefined)

  return {
    policyId,
    dwellingType,
    startAt,
    ownerName: userProfile?.fullName ?? userProfile?.givenName ?? '',
    policiesLoading,
    isModalOpen,
    closeModal,
    currentStep,
    handleNext,
    handleBack,
    isLastStep: currentStep === ONBOARDING_STEP_COUNT - 1,
    handleManageCoverage,
    additionalCoverageTypes,
    planName: dwellingType,
    isEnglish: currentLanguage === 'en',
  }
}
